/**
 * The flights in the air, drawn on the globe as decoration.
 *
 * The home document is baked, so a leg arrives as two positions and the window it flies in; where
 * the aircraft is *at this moment* is this module's own arithmetic — from the shared leg geometry
 * the dashboard map already uses (`interpolateLeg`, `legProgressAt`, `legPhaseAt`), so one
 * definition of where a leg is serves both surfaces.
 *
 * Four deliberate choices, all of them about staying out of the page's way:
 *
 * - The layers are MapLibre symbol and line layers, added **before** the city names, because a
 *   layer added later paints above: the routes and the aircraft under the text, never over it.
 * - The source is refreshed on a slow tick rather than every frame. A leg crosses a handful of
 *   pixels a minute at the closest chapter's zoom, so a per-frame re-place would be motion nobody
 *   can see, at an allocation and a `setData` every frame. The globe turning is the camera's own
 *   per-frame write, which moves everything drawn on the map for free.
 * - Prominence is opacity plus ink, never size: two icons of one geometry carry the resting and the
 *   accent ink, so a hovered aircraft is the same shape at the same size as a resting one — and the
 *   city the reader is on also gets its routes drawn as hairlines under the glyphs. The accent lives
 *   on layers of its own, chosen by a filter and drawn at a constant opacity, because that is the
 *   one shape of change MapLibre will interpolate: the ink ramps in and out instead of blinking.
 * - Aircraft that would land on top of another are dropped rather than piled up, and the glyph is
 *   drawn smaller as the camera pulls back, so the finale's small Earth is not crowded by a fleet
 *   sized for the opening's large one. The drop is decided here rather than left to MapLibre, which
 *   only refuses glyphs whose boxes *intersect*: a bundle of near-parallel routes reads as a pile
 *   long before the boxes touch, so the floor between two drawn glyphs is this module's own.
 * - Every route is drawn as a gentle bow rather than the great circle's near-straight line, and
 *   near-parallel routes are fanned onto lanes of their own: the reader asked for spacing, so two
 *   different routes into one city never share a line, while two flights a day on one city pair
 *   deliberately still do.
 * - Each route carries an airport glyph at both ends, on a layer under the names with the routes,
 *   and the pointer can be answered with a flight card or an airport code. The pointer is read on
 *   `window`, not on the map: the globe is deliberately inert (`pointer-events: none` on the
 *   stage, `interactive: false` on the map) so it can never swallow the page's wheel, drag or text
 *   selection, and a decoration that eats the pointer would be worse than one nobody can hover.
 */

import type * as MaplibreGl from "maplibre-gl";

import { interpolateLeg, legPhaseAt, legProgressAt, seatbeltOn } from "@noko-city/shared";

import type { LocatedCity } from "@/components/atlas-camera";
import { byKey, declutter, type PlacedLike } from "@/components/atlas-declutter";
import {
  PLANE_ART_PX,
  PLANE_PIXEL_RATIO,
  planeImage,
  planeSizeAt,
  planeSizeRamp,
  PORT_ICON_PX,
  PORT_ICON_SIZE,
  portImage,
} from "@/components/atlas-glyph-art";
import {
  LEG_HOVER_OPACITY,
  LEG_LAYER,
  LEG_SOURCE,
  LEG_WIDTH,
  type LegFeature,
  legFeatures,
  legGradient,
  legKey,
  type PlacedLeg,
  stampLanes,
} from "@/components/atlas-legs";
import {
  declutterPorts,
  PORT_BASE_OPACITY,
  PORT_HOVER_ICON,
  PORT_HOVER_LAYER,
  PORT_HOVER_OPACITY,
  PORT_ICON,
  PORT_LAYER,
  PORT_SOURCE,
  type PortFeature,
  portFeatures,
} from "@/components/atlas-ports";

const PLANE_SOURCE = "noko-flights";
const PLANE_LAYER = "noko-flights";
/**
 * The hovered city's aircraft, on a layer of its own, above the fleet and under the names. The ink
 * has to be a plain number on a layer whose feature set a filter chooses — MapLibre drops its
 * transition outright for a value that depends on the feature (see `applyHighlight`), so the accent
 * art cannot ride a per-feature `case` expression and ramp at the same time.
 */
const PLANE_HOVER_LAYER = "noko-flights-hot";
/**
 * A filter that matches nothing. The ink's layers carry a selection only while one is set: at rest
 * there is no city to draw, and the routes are the hover's own signal rather than an always-on layer.
 */
const NEVER_MATCHES: MaplibreGl.ExpressionSpecification = ["boolean", false];
/** The silhouette in the resting ink, and the same silhouette in the hover ink. */
const PLANE_ICON = "noko-plane";
const PLANE_HOVER_ICON = "noko-plane-hot";

/**
 * The globe's own coastline ink. The planes are the page's decoration, not its content: at rest
 * they take the palette's light grey hairline tone, and only the city the reader is standing on is
 * drawn in the accent its own name is written in.
 */
const PLANE_INK = "#C8C8D0";
/** Muted on purpose: the fleet is context, and the eye is meant to stay on the names. */
const PLANE_BASE_OPACITY = 0.28;
/** The city whose directory row is hovered or focused: its aircraft, and only its, in the accent. */
const PLANE_HOVER_OPACITY = 0.8;

/**
 * How much room one glyph reserves, as a multiple of its own drawn size: at this spacing two
 * aircraft keep a gap of three fifths of a glyph between them, which is what "not touching" means
 * to a reader. MapLibre's own `icon-allow-overlap: false` is kept on as a second line of defence
 * and cannot do this alone: it drops only boxes that *intersect*, and a bundle reads as a pile
 * long before that.
 */
const PLANE_CLEARANCE = 1.6;
/**
 * How far the zoom must move before the drawn set is recomputed. The clearance is a screen
 * distance, so only a zoom change can re-decide it — a rotation cannot, and the tick re-decides
 * anyway. Five hundredths of a zoom level moves the spacing by under four percent.
 */
const DECLUTTER_ZOOM_STEP = 0.05;

/**
 * How often the placed set is recomputed. Ten seconds is under two pixels of travel at the closest
 * chapter's zoom, so the aircraft read as moving continuously while the source is left alone the
 * rest of the time.
 */
const PLANE_TICK_MS = 10_000;

/**
 * The hover ink's own ramp. The page's selection vocabulary is 240-260 ms — the chapter
 * cross-dissolve, the row's own bar — and the ink is the same family of change: a route that
 * snaps on reads as a different state rather than as the row's own emphasis. MapLibre runs this
 * on a clock of its own, so it is the one motion on the page the reduced-motion CSS cannot
 * reach; the query in `createAtlasPlanes` is what keeps that promise.
 */
const INK_FADE_MS = 260;

/**
 * One aircraft as the source carries it: where it is, the course its nose points along, and the
 * little a card can say about it. `city` is the name of the city it is arriving into — the key the
 * hover prominence matches on — and `key` names the leg itself, so the resting ink and the hover
 * ink are one aircraft as far as the pointer is concerned even though they are two features.
 */
export type PlaneFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    city: string;
    heading: number;
    key: string;
    /** The seatbelt sign as a number: a symbolic layer takes numbers, and the card reads it back. */
    belt: number;
    /** The flight as a reader writes it, `""` when the store knows no code and number. */
    number: string;
    /** The two airports' IATA codes, `""` when the store knows none. */
    fromCode: string;
    toCode: string;
    /** The takeoff instant in ms: the declutter's tie-break, so the older flight keeps its glyph. */
    takeoff: number;
  };
};

/**
 * The payload's legs, parsed, flattened across cities and given their lanes.
 *
 * A leg whose instants do not parse is dropped rather than drawn: the wire decoder is the gate for
 * the payload's shape, and this is the last guard before arithmetic, because an Invalid Date would
 * reach `legProgressAt` as a NaN and place an aircraft at a coordinate nobody published.
 */
export const placedLegs = (cities: readonly LocatedCity[]): PlacedLeg[] => {
  const legs: PlacedLeg[] = [];
  for (const city of cities) {
    for (const leg of city.flightLegs ?? []) {
      const takeoffAt = new Date(leg.takeoffAt);
      const landingAt = new Date(leg.landingAt);
      if (!Number.isFinite(takeoffAt.getTime()) || !Number.isFinite(landingAt.getTime())) continue;
      legs.push({
        city: city.name,
        from: { lat: leg.fromLat, lng: leg.fromLng },
        to: { lat: leg.toLat, lng: leg.toLng },
        takeoffAt,
        landingAt,
        number: leg.designator ?? "",
        fromCode: leg.fromCode ?? "",
        toCode: leg.toCode ?? "",
        laneRadians: 0,
      });
    }
  }
  stampLanes(legs);
  return legs;
};

/**
 * Every leg in the air at `now`, placed and pointed.
 *
 * `legPhaseAt` with no status is the same clock rule the board's own phase badge uses, so a leg
 * that has landed is gone on the next tick without anything else having to notice. Position and
 * course come back from one call, on the leg's own lane: an aircraft drawn on the great circle but
 * pointed along some other bearing — or bowing beside the line that claims to be its route — is the
 * one way a silhouette can look broken.
 */
export const planeFeatures = (legs: readonly PlacedLeg[], now: Date): PlaneFeature[] => {
  const features: PlaneFeature[] = [];
  for (const leg of legs) {
    if (legPhaseAt(leg, null, now) !== "in_progress") continue;
    const at = interpolateLeg(leg.from, leg.to, legProgressAt(leg, now), leg.laneRadians);
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [at.longitude, at.latitude] },
      properties: {
        city: leg.city,
        heading: at.bearingDegrees,
        key: legKey(leg),
        belt: seatbeltOn(leg, now) ? 1 : 0,
        number: leg.number,
        fromCode: leg.fromCode,
        toCode: leg.toCode,
        takeoff: leg.takeoffAt.getTime(),
      },
    });
  }
  return features;
};

/**
 * The aircraft that are drawn: the city the reader is standing on first, then the oldest flight,
 * each kept only when it clears every glyph already kept.
 */
export const declutterPlanes = (
  planes: readonly PlaneFeature[],
  project: (coordinates: [number, number]) => { x: number; y: number },
  glyphPx: number,
  activeCity: string | null,
): PlaneFeature[] =>
  declutter(
    planes,
    project,
    glyphPx * PLANE_CLEARANCE,
    (a, b) =>
      (a.properties.city === activeCity ? 0 : 1) - (b.properties.city === activeCity ? 0 : 1) ||
      a.properties.takeoff - b.properties.takeoff ||
      byKey(a, b),
  );

/**
 * What the pointer is on, if anything: the drawn aircraft and airport glyphs tested against one
 * point, the nearest, an aircraft preferred to an airport.
 *
 * Both ends of the union carry the glyph's own coordinates, so a card can follow it while the globe
 * turns underneath the pointer without this module knowing anything about cards.
 */
export type FlightHit =
  | {
      kind: "plane";
      coordinates: [number, number];
      city: string;
      /** The flight as a reader writes it, `""` when the store knows no code and number. */
      number: string;
      fromCode: string;
      toCode: string;
      belt: boolean;
    }
  | {
      kind: "port";
      coordinates: [number, number];
      city: string;
      /** The airport's IATA code, `""` when the store knows none. */
      code: string;
      /** The glyph's own key, so the hovered one can take the accent ink. */
      key: string;
    };

/** How much of a glyph is hit, as a multiple of its drawn box: the box, plus a small edge. */
const HIT_SIZE = 1.15;

export interface AtlasPlanes {
  /** Rebuild the leg set from the payload: on mount, and on every payload change. */
  setCities: (cities: readonly LocatedCity[]) => void;
  /**
   * The city whose directory row is hovered or focused: its inbound aircraft take the accent ink,
   * its routes are drawn and its airports labelled. Any other value — null included — restores the
   * muted floor with no routes and no airport glyphs at all.
   */
  setHighlight: (cityName: string | null) => void;
  /**
   * Re-decide what may be drawn, after the camera moved. Called once per camera frame; it returns
   * immediately unless the zoom stepped far enough to matter, because the floor it enforces is a
   * screen distance and a rotation cannot change one.
   */
  update: () => void;
  /**
   * What the pointer is on, if anything: the drawn aircraft and airport glyphs, the nearest, an
   * aircraft preferred to an airport. `point` is canvas-local — the same frame `map.project`
   * returns — because the globe itself is never handed a pointer event to answer with.
   */
  hitTest: (point: { x: number; y: number }) => FlightHit | null;
  /** The airport glyph the pointer is on, which takes the accent ink; null for none. */
  setPortHover: (key: string | null) => void;
  dispose: () => void;
}

/**
 * Create the plane layer on an already-loaded style, and return the three statements the
 * controller needs.
 *
 * The decoration is best-effort by contract: if the icon or the layer cannot be created, the globe
 * keeps its names and loses its aircraft rather than failing the page.
 *
 * `accent` is the page's own token, read from the DOM by the caller: it is the ink of the
 * highlighted aircraft and of that leg's route, the one orange this page draws.
 */
export const createAtlasPlanes = (map: MaplibreGl.Map, accent: string): AtlasPlanes => {
  let legs: PlacedLeg[] = [];
  let highlight: string | null = null;
  let ready = false;
  /**
   * What is painted, which is the declutter's own output: kept here so the pointer is answered from
   * the glyphs actually on screen rather than from a second, separately-filtered read of the
   * renderer's own hit boxes.
   */
  let drawnPlanes: PlaneFeature[] = [];
  let drawnPorts: PortFeature[] = [];
  /** The window's legs as placed, before anything decides which of them may be drawn. */
  let livePlanes: PlaneFeature[] = [];
  let livePorts: PortFeature[] = [];
  /** The zoom the painted set was last decided at, and the airport glyph the pointer is over. */
  let declutterZoom = Number.NEGATIVE_INFINITY;
  let hoveredPort: string | null = null;
  /**
   * The one motion on this page the reduced-motion CSS cannot reach: MapLibre runs its own
   * transitions in JavaScript, so the ink's ramp is gated here instead of in a stylesheet.
   */
  const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  /** The sets the three sources currently hold, as strings, so an unchanged tick writes nothing. */
  let writtenPlanes = "";
  let writtenRoutes = "";
  let writtenPorts = "";

  try {
    const image = planeImage(PLANE_INK);
    const hoverImage = planeImage(accent);
    const port = portImage(PLANE_INK);
    const portHover = portImage(accent);
    if (!image || !hoverImage || !port || !portHover) {
      throw new Error("no 2D canvas context for the flight decoration");
    }
    map.addImage(PLANE_ICON, image, { pixelRatio: PLANE_PIXEL_RATIO });
    map.addImage(PLANE_HOVER_ICON, hoverImage, { pixelRatio: PLANE_PIXEL_RATIO });
    map.addImage(PORT_ICON, port, { pixelRatio: PLANE_PIXEL_RATIO });
    map.addImage(PORT_HOVER_ICON, portHover, { pixelRatio: PLANE_PIXEL_RATIO });

    // The routes go on first, and the layer order is the whole reason: a line layer added before the
    // glyphs paints under them, every glyph goes under the city names because the names are added
    // after this module returns, and the two hover inks go above the resting decoration so the
    // answer to the pointer is never buried by the thing it is pointing at.
    map.addSource(LEG_SOURCE, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
      // The gradient below is drawn along each line's own progress, which the renderer can only
      // measure when it is asked to.
      lineMetrics: true,
    });
    map.addLayer({
      id: LEG_LAYER,
      type: "line",
      source: LEG_SOURCE,
      // The routes of the city the reader is on, and no other: the filter is the selection.
      filter: NEVER_MATCHES,
      layout: { "line-cap": "round", "line-join": "round" },
      // Zero until a city is hovered or focused, and a plain number rather than a per-feature
      // expression, so MapLibre will interpolate it: the routes fade away as the row's own bar does
      // instead of blinking out on the frame the pointer leaves.
      paint: {
        "line-gradient": legGradient(accent),
        "line-width": LEG_WIDTH,
        "line-opacity": 0,
        "line-opacity-transition": { duration: INK_FADE_MS, delay: 0 },
      },
    });

    map.addSource(PLANE_SOURCE, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    map.addLayer({
      id: PLANE_LAYER,
      type: "symbol",
      source: PLANE_SOURCE,
      layout: {
        "icon-image": PLANE_ICON,
        "icon-size": planeSizeRamp(),
        // The course, in map degrees: a plane must turn with the globe under it, not with the
        // screen. Overlap is refused rather than ignored, so an aircraft that would land on top of
        // another is dropped. Placement walks the layers top-down, so the city names — added after
        // this layer, and therefore above it — are placed first and always win: a glyph only ever
        // loses to a name or to an aircraft placed before it.
        "icon-rotate": ["get", "heading"],
        "icon-rotation-alignment": "map",
        // The declutter is this module's own, and this flag is what makes the renderer agree with
        // it: MapLibre's pass would drop a glyph the declutter kept, and the pointer is answered
        // from the same painted set (`hitTest` reads `drawnPlanes`), so a hidden glyph would be one
        // the reader cannot see and can still hover. A drawn glyph always beats a collision: the
        // clearance above is deliberately wider than touching, and the city names are DOM markers
        // above the canvas, so they were never in MapLibre's placement to lose to.
        "icon-allow-overlap": true,
      },
      // The resting fleet's own weight, and a constant: the accent never touches this layer, so
      // there is nothing here to transition.
      paint: { "icon-opacity": PLANE_BASE_OPACITY },
    });

    // The hover ink: the accent art of the one city the reader is on, above the fleet and under the
    // names, with a constant opacity that ramps. `icon-allow-overlap` keeps the ink out of the
    // declutter — the aircraft the reader asked about is the one that must not be dropped — and
    // `icon-ignore-placement` keeps it from hiding the resting glyph underneath, which is what makes
    // the release a cross-fade back to grey rather than a swap.
    map.addLayer({
      id: PLANE_HOVER_LAYER,
      type: "symbol",
      source: PLANE_SOURCE,
      filter: NEVER_MATCHES,
      layout: {
        "icon-image": PLANE_HOVER_ICON,
        "icon-size": planeSizeRamp(),
        "icon-rotate": ["get", "heading"],
        "icon-rotation-alignment": "map",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        "icon-opacity": 0,
        "icon-opacity-transition": { duration: INK_FADE_MS, delay: 0 },
      },
    });

    // The aerodromes of the city the reader is on: one glyph per route end, above the fleet and
    // under the names. Fixed points, so placement is decided by this module's own declutter rather
    // than by the renderer's — `icon-allow-overlap` keeps every glyph the declutter kept, and
    // `icon-ignore-placement` keeps it from hiding whatever is under it.
    map.addSource(PORT_SOURCE, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    map.addLayer({
      id: PORT_LAYER,
      type: "symbol",
      source: PORT_SOURCE,
      filter: NEVER_MATCHES,
      layout: {
        "icon-image": PORT_ICON,
        "icon-size": PORT_ICON_SIZE,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        "icon-opacity": 0,
        "icon-opacity-transition": { duration: INK_FADE_MS, delay: 0 },
      },
    });

    // The hovered aerodrome's own ink, above everything else the decoration paints, for the same
    // reason the aircraft's hover ink is: the answer to the pointer must not be under the thing the
    // pointer is on.
    map.addLayer({
      id: PORT_HOVER_LAYER,
      type: "symbol",
      source: PORT_SOURCE,
      filter: NEVER_MATCHES,
      layout: {
        "icon-image": PORT_HOVER_ICON,
        "icon-size": PORT_ICON_SIZE,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: {
        "icon-opacity": 0,
        "icon-opacity-transition": { duration: INK_FADE_MS, delay: 0 },
      },
    });
    ready = true;
  } catch (error) {
    console.error("[noko-map] plane layer failed; the globe draws no aircraft", error);
  }

  /**
   * The fade's own gate. The page drops every CSS transition under reduced motion; a MapLibre
   * transition is JavaScript and survives that, so without this the ink would be the one thing left
   * on the page that still animated.
   *
   * The reduced duration is 1 ms rather than 0 for a measured reason: MapLibre leaves a paint change
   * carrying a zero-duration transition unrendered until something else dirties the style — with the
   * ink's own layers the value was set and simply never drew until the reader moved the pointer
   * somewhere that forced a frame. One millisecond is done before the next frame, so the ink still
   * appears and leaves in a single step, which is what reduced motion asks for.
   */
  const applyFade = (): void => {
    if (!ready) return;
    const transition = { duration: reducedQuery.matches ? 1 : INK_FADE_MS, delay: 0 };
    map.setPaintProperty(LEG_LAYER, "line-opacity-transition", transition);
    map.setPaintProperty(PORT_LAYER, "icon-opacity-transition", transition);
    map.setPaintProperty(PLANE_HOVER_LAYER, "icon-opacity-transition", transition);
    map.setPaintProperty(PORT_HOVER_LAYER, "icon-opacity-transition", transition);
  };
  applyFade();
  reducedQuery.addEventListener("change", applyFade);

  /**
   * One write in flight per source: a slower update never piles up behind a faster one. Each layer
   * owns a queue of its own, so a burst of route changes cannot make the aircraft wait for the
   * routes.
   */
  const queueFor = <Feature extends PlaneFeature | LegFeature | PortFeature>(sourceId: string) => {
    let pending: Feature[] | null = null;
    let draining = false;
    const write = async (features: Feature[]): Promise<void> => {
      try {
        await map.getSource<MaplibreGl.GeoJSONSource>(sourceId)?.setData({
          type: "FeatureCollection",
          features,
        });
      } catch (error) {
        console.error("[noko-map] flight decoration update failed", error);
      }
    };
    return (features: Feature[]): void => {
      pending = features;
      if (draining) return;
      draining = true;
      void (async () => {
        try {
          while (pending !== null) {
            const next = pending;
            pending = null;
            await write(next);
          }
        } finally {
          draining = false;
        }
      })();
    };
  };

  const pushPlanes = queueFor<PlaneFeature>(PLANE_SOURCE);
  const pushRoutes = queueFor<LegFeature>(LEG_SOURCE);
  const pushPorts = queueFor<PortFeature>(PORT_SOURCE);

  /**
   * The aircraft as painted: the declutter's own set, written only when the set actually changed.
   * Identity by position, rounded to the precision the source can show, so a tick that moved nothing
   * visible leaves the source alone — which is most of them.
   */
  const paintPlanes = (): void => {
    if (!ready) return;
    const zoom = map.getZoom();
    declutterZoom = zoom;
    drawnPlanes = declutterPlanes(
      livePlanes,
      (coordinates) => map.project(coordinates),
      PLANE_ART_PX * planeSizeAt(zoom),
      highlight,
    );
    const next = drawnPlanes
      .map(
        (plane) =>
          `${plane.properties.key}:${plane.geometry.coordinates[0].toFixed(4)}:${plane.geometry.coordinates[1].toFixed(4)}`,
      )
      .join("|");
    if (next === writtenPlanes) return;
    writtenPlanes = next;
    pushPlanes(drawnPlanes);
  };

  /**
   * The aerodromes as painted: the same decision at their own size, from coordinates that never move
   * between ticks, so the signature is the set of keys alone.
   */
  const paintPorts = (): void => {
    if (!ready) return;
    drawnPorts = declutterPorts(
      livePorts,
      (coordinates) => map.project(coordinates),
      PORT_ICON_PX,
      highlight,
    );
    const next = drawnPorts.map((port) => port.properties.key).join("|");
    if (next === writtenPorts) return;
    writtenPorts = next;
    pushPorts(drawnPorts);
  };

  const refresh = (): void => {
    if (!ready) return;
    const now = new Date();

    // Routes only ever enter and leave — they do not move between ticks — so their own signature is
    // what keeps a tick from re-writing geometry that has not changed. The lane rides the signature
    // because a route whose lane moved is a route whose geometry moved.
    const routes = legFeatures(legs, now);
    const nextRoutes = routes
      .map(
        (feature) =>
          `${feature.properties.city}|${feature.properties.route}|${feature.properties.lane.toFixed(8)}`,
      )
      .join("|");
    if (nextRoutes !== writtenRoutes) {
      writtenRoutes = nextRoutes;
      pushRoutes(routes);
    }

    livePlanes = planeFeatures(legs, now);
    livePorts = portFeatures(legs, now);
    paintPlanes();
    paintPorts();
  };

  /**
   * The camera's own re-decision. The floor between two glyphs is a screen distance, so a rotation
   * cannot change the answer and a zoom can: this returns unless the zoom has stepped far enough to
   * matter, which keeps a drag free of both projections and writes.
   */
  const update = (): void => {
    if (!ready) return;
    if (Math.abs(map.getZoom() - declutterZoom) < DECLUTTER_ZOOM_STEP) return;
    paintPlanes();
    paintPorts();
  };

  /**
   * The hover write. Re-derived from the stored selection rather than only on hover, so a payload
   * change cannot drop a highlight the reader is still standing on.
   *
   * The selection rides the layers' own filters and the ink rides their opacities, which is the only
   * arrangement MapLibre can ramp at all: `TransitioningPropertyValue.possiblyEvaluate` throws the
   * transition away the moment the incoming value is data-driven — a `["case", ["==", ["get",
   * "city"], …], …]` opacity compiles to exactly that — so a per-feature ink snaps at any configured
   * duration, while a constant interpolates.
   *
   * A release therefore writes no filter at all: the features stay on the city the reader just left
   * and only the ink ramps away. Clearing the filter here would cut the fade off on its first frame.
   * Leaving it costs nothing — the routes are a handful of vertices at zero opacity, and the ink's
   * glyphs neither block the resting fleet (`icon-ignore-placement`) nor are blocked by it.
   */
  const applyHighlight = (): void => {
    if (!ready) return;
    if (highlight !== null) {
      const mine: MaplibreGl.ExpressionSpecification = ["==", ["get", "city"], highlight];
      map.setFilter(LEG_LAYER, mine);
      map.setFilter(PORT_LAYER, mine);
      map.setFilter(PLANE_HOVER_LAYER, mine);
    }
    map.setPaintProperty(LEG_LAYER, "line-opacity", highlight === null ? 0 : LEG_HOVER_OPACITY);
    map.setPaintProperty(PORT_LAYER, "icon-opacity", highlight === null ? 0 : PORT_BASE_OPACITY);
    map.setPaintProperty(
      PLANE_HOVER_LAYER,
      "icon-opacity",
      highlight === null ? 0 : PLANE_HOVER_OPACITY,
    );
    // The selected city is also the declutter's first priority among airports, so a selection
    // changes which ends survive a crowded corner as well as which are admitted at all.
    paintPorts();
  };

  /**
   * What the pointer is on, read off the glyphs this module has actually drawn rather than off the
   * renderer: `queryRenderedFeatures` would need every layer's filter and every image's real size
   * re-derived from a second place, while the painted sets are already this module's own output.
   *
   * The globe is never handed a pointer event to answer with — MapSection's map is not interactive
   * and the stage around it takes no pointer events — so the caller reads its own window, converts
   * to canvas-local coordinates, and asks. Aircraft are tested before airports and win outright: an
   * aircraft sitting on the airport it just left is the more specific answer.
   */
  const hitTest = (point: { x: number; y: number }): FlightHit | null => {
    if (!ready) return null;
    const nearest = <Feature extends PlacedLike>(
      features: readonly Feature[],
      radius: number,
      accept: (feature: Feature) => boolean = () => true,
    ): Feature | null => {
      let found: Feature | null = null;
      let best = radius;
      for (const feature of features) {
        if (!accept(feature)) continue;
        const at = map.project(feature.geometry.coordinates);
        const distance = Math.hypot(at.x - point.x, at.y - point.y);
        if (distance > best) continue;
        found = feature;
        best = distance;
      }
      return found;
    };

    const planeRadius = (PLANE_ART_PX * planeSizeAt(map.getZoom()) * HIT_SIZE) / 2;
    const plane = nearest(drawnPlanes, planeRadius);
    if (plane) {
      return {
        kind: "plane",
        coordinates: plane.geometry.coordinates,
        city: plane.properties.city,
        number: plane.properties.number,
        fromCode: plane.properties.fromCode,
        toCode: plane.properties.toCode,
        belt: plane.properties.belt === 1,
      };
    }

    // Only the selected city's airports are drawn at all, so nothing else is here to be hit.
    if (highlight === null) return null;
    const port = nearest(
      drawnPorts,
      (PORT_ICON_PX * HIT_SIZE) / 2,
      (feature) => feature.properties.city === highlight,
    );
    if (!port) return null;
    return {
      kind: "port",
      coordinates: port.geometry.coordinates,
      city: port.properties.city,
      code: port.properties.code,
      key: port.properties.key,
    };
  };

  const timer = window.setInterval(refresh, PLANE_TICK_MS);

  return {
    setCities: (cities) => {
      legs = placedLegs(cities);
      // A new payload makes every written set stale by definition, whatever the strings say.
      writtenPlanes = "";
      writtenRoutes = "";
      writtenPorts = "";
      refresh();
      applyHighlight();
    },
    setHighlight: (cityName) => {
      if (cityName === highlight) return;
      highlight = cityName;
      applyHighlight();
    },
    update,
    hitTest,
    setPortHover: (key) => {
      if (!ready || key === hoveredPort) return;
      hoveredPort = key;
      // A release writes no filter, for the same reason the aircraft's ink does not: the accent
      // ramps away from the glyph it was on instead of being cut off on its first frame.
      if (key !== null) map.setFilter(PORT_HOVER_LAYER, ["==", ["get", "key"], key]);
      map.setPaintProperty(PORT_HOVER_LAYER, "icon-opacity", key === null ? 0 : PORT_HOVER_OPACITY);
    },
    dispose: () => {
      reducedQuery.removeEventListener("change", applyFade);
      window.clearInterval(timer);
      legs = [];
      livePlanes = [];
      livePorts = [];
      drawnPlanes = [];
      drawnPorts = [];
      if (!ready) return;
      ready = false;
      try {
        // Layers before their sources, and in the reverse of the order they were added.
        if (map.getLayer(PORT_HOVER_LAYER)) map.removeLayer(PORT_HOVER_LAYER);
        if (map.getLayer(PLANE_HOVER_LAYER)) map.removeLayer(PLANE_HOVER_LAYER);
        if (map.getLayer(PORT_LAYER)) map.removeLayer(PORT_LAYER);
        if (map.getLayer(PLANE_LAYER)) map.removeLayer(PLANE_LAYER);
        if (map.getLayer(LEG_LAYER)) map.removeLayer(LEG_LAYER);
        if (map.getSource(PORT_SOURCE)) map.removeSource(PORT_SOURCE);
        if (map.getSource(PLANE_SOURCE)) map.removeSource(PLANE_SOURCE);
        if (map.getSource(LEG_SOURCE)) map.removeSource(LEG_SOURCE);
      } catch (error) {
        // A torn-down style takes its own layers with it; there is nothing left to remove.
        console.error("[noko-map] flight decoration removal skipped", error);
      }
    },
  };
};
